import { normalizeMembershipRoles, ROLE_GRANTS } from '../models/roles';
import { makeObservable, observable, reaction, runInAction } from 'mobx';
import { dataManager } from '../APIEntity';
import { ApiResponse } from '../ApiResponse';
import { EntityTypes } from '../schema/types';
import { dataContext, TypeId } from '../FlowSync';
import { ActionInfo, IMembershipRequest, Membership, MentionSendInfo, UserRole } from '../models';
import { navigator } from './navigationService';

/**
 * Direct member selector — one of these keys identifies the member to act on.
 * Supersedes the legacy ``{member_through, value}`` envelope on both the remove
 * (DELETE) and role-update (PUT) paths: the discriminator was redundant since
 * the hub immediately fanned ``value`` back out into exactly these fields. The
 * 95% case is just ``{user_id}``. The hub still tolerates the old envelope.
 */
interface MembershipIdentifiers {
  user_id?: string;
  user_email?: string;
  invitation_id?: string;
}

interface MembershipRoleUpdate extends MembershipIdentifiers {
  roles: string[];
}

class RolesLabel {
  getRole(role: string | undefined) {
    switch (role) {
      case 'owner':
      case 'admin':
        return 'admin';
      case 'editor':
        return 'editor';
      case 'reader':
        return 'reader';
      case 'runner':
        return 'runner';
      default:
        return 'visitor';
    }
  }

  getRoleDescription(role: UserRole) {
    switch (role) {
      case 'owner':
      case 'admin':
        return 'Full Access';
      case 'editor':
        return 'Can edit';
      case 'reader':
        return 'Can view';
      case 'runner':
        return 'Can run';
      default:
        return role; // TODO this is temp till role hierarchy is implemented. Then it should change to throwing an error or writing 'Fix me please';
      // return 'Fix me please';
    }
  }

  getRoleDescriptionHelpText(role: UserRole) {
    switch (role) {
      case 'owner':
      case 'admin':
        return 'Can edit and share with others.';
      case 'editor':
        return 'Can edit, but not share with others.';
      case 'reader':
        return 'Cannot edit or share with others.';
      case 'runner':
        return 'Can run without viewing';
      default:
        return 'This should throw an error';
    }
  }

  getLocalMemberDescription(role: UserRole, entityType: string): string {
    let roleDescription;
    switch (role) {
      case 'owner':
      case 'admin':
        roleDescription = 'full access';
        break;
      case 'editor':
        roleDescription = 'edit access';
        break;
      case 'reader':
        roleDescription = 'view access';
        break;
      case 'runner':
        roleDescription = 'run access';
        break;
      default:
        roleDescription = 'view access';
    }
    return `You have ${roleDescription} to this ${entityType}`;
  }
}

class MentionService {
  async sendMention(entity: any, mentionPayload: MentionSendInfo): Promise<void> {
    try {
      const actionInfo = new ActionInfo('send_mention', entity.type, entity.id, 'POST');
      actionInfo.bodyParameters = mentionPayload as any;
      await dataManager.callAction<MentionSendInfo, undefined>(actionInfo);
    } catch (e) {
      console.error('Failed to send mention: ', e);
    }
  }
}

class MembershipService {
  activeWorkspaceMemberships: Membership[] = [];
  activeEntityMemberships: Membership[] = [];

  constructor() {
    makeObservable(this, {
      activeWorkspaceMemberships: observable,
      activeEntityMemberships: observable,
    });
    reaction(
      () => dataContext.activeEntity,
      () => this.recalculateActiveEntityMemberships(),
    );
    reaction(
      () => dataContext.workspace,
      () => this.recalculateActiveWorkspaceMemberships(),
    );
  }

  async fetchPendingMemberships(typeId: TypeId): Promise<Membership[] | undefined> {
    return (await membershipService.fetchMemberships(typeId))?.filter(
      (member: Membership) => member.status === 'pending',
    );
  }

  async fetchMemberships(entity_typeId: TypeId): Promise<Membership[]> {
    if (entity_typeId.type == EntityTypes.Workspace) {
      const workspace = dataManager.getByTypeIdFromCache(entity_typeId);
      const roles = workspace?.expand?.roles;
      if (!roles?.length) {
        return [] as Membership[];
      }
    }

    try {
      return await this.readMemberships(entity_typeId, false);
    } catch (error) {
      console.error('Failed to fetch memberships', error);
      return [];
    }
  }

  /** A failed roster is unknown membership, never proof that nobody belongs. */
  async fetchMembershipsStrict(entityTypeId: TypeId): Promise<Membership[]> {
    return this.readMemberships(entityTypeId, true);
  }

  private async readMemberships(entityTypeId: TypeId, requireAuthoritative: boolean): Promise<Membership[]> {
    const actionInfo = new ActionInfo('members', entityTypeId.type, entityTypeId.id, 'GET');
    actionInfo.hubReflect = true;
    if (requireAuthoritative) actionInfo.queryParameters = { require_authoritative: true };
    const roster = await dataManager.callAction<undefined, Membership[]>(actionInfo);
    if (!Array.isArray(roster)) throw new Error('The server did not return project members.');
    // Desktop reflection normalizes hub user_email/user_name into email/name.
    // Restore the membership service's public shape for all roster consumers.
    return roster.map((member) => {
      const reflected = member as Membership & { email?: string; name?: string };
      return {
        ...member,
        user_email: member.user_email ?? reflected.email ?? '',
        user_name: member.user_name ?? reflected.name,
      };
    });
  }

  /** Grantable choices from the generated canonical hub role registry. */
  async fetchShareRoles(): Promise<string[]> {
    return [...ROLE_GRANTS.owner];
  }

  async createMembership(entity_typeId: TypeId, membershipRequest: IMembershipRequest): Promise<void> {
    const request = {
      ...membershipRequest,
      invitation_targets: membershipRequest.invitation_targets.map(target => ({
        ...target, roles: normalizeMembershipRoles(target.roles),
      })),
    };
    try {
      const actionInfo = new ActionInfo('members', entity_typeId.type, entity_typeId.id, 'POST');
      actionInfo.hubReflect = true; // membership change is hub-owned — reflect to the hub
      actionInfo.bodyParameters = request;
      await dataManager.callAction<IMembershipRequest, ApiResponse<void>>(actionInfo);
    } catch (error) {
      if (error instanceof Error) {
        console.error(`Failed to create membership: ${error.message}`);
      } else {
        console.error('Failed to create membership: Unknown error');
      }
    }
  }

  /** Pick the single identifier key for a member — the body shape for the
   * remove (DELETE) and role-update (PUT) paths. */
  determineMembershipIdentifiers(membership: Membership): MembershipIdentifiers {
    if (membership.user_id) {
      return { user_id: membership.user_id };
    } else if (membership.user_email) {
      return { user_email: membership.user_email };
    } else if (membership.invitation_id) {
      return { invitation_id: membership.invitation_id };
    } else {
      throw new Error('No valid membership identifier could be determined');
    }
  }

  async updateMembershipRole(entity_typeId: TypeId, membership: Membership) {
    if (!membership.user_id && !membership.user_email && !membership.invitation_id) {
      console.error('At least one of user_id, user_email, or invitation_id is required');
      return;
    }
    if (!membership.roles?.length) {
      console.error('membership.roles is required');
      return;
    }

    const roles = normalizeMembershipRoles(membership.roles);
    try {
      // PUT sends the identifier directly alongside the role — no
      // ``{member_through, value}`` envelope. The hub accepts both shapes.
      const payload: MembershipRoleUpdate = {
        ...this.determineMembershipIdentifiers(membership),
        roles,
      };

      const actionInfo = new ActionInfo('members', entity_typeId.type, entity_typeId.id, 'PUT');
      actionInfo.hubReflect = true; // role change is hub-owned — reflect to the hub
      actionInfo.bodyParameters = payload as any;
      await dataManager.callAction<MembershipRoleUpdate, ApiResponse<void>>(actionInfo);
    } catch (error) {
      console.error('Failed to update Membership', error);
    }
  }

  async removeMembership(entity_typeId: TypeId, membership: Membership) {
    if (!membership.user_id && !membership.user_email && !membership.invitation_id) {
      console.error('At least one of user_id, user_email, or invitation_id is required');
      return;
    }

    try {
      // Remove sends identifiers directly — ``{user_id}`` / ``{user_email}`` /
      // ``{invitation_id}`` — instead of the legacy ``{member_through, value}``
      // envelope. The hub still accepts both shapes during the transition.
      const payload: MembershipIdentifiers = this.determineMembershipIdentifiers(membership);

      const actionInfo = new ActionInfo('members', entity_typeId.type, entity_typeId.id, 'DELETE');
      actionInfo.hubReflect = true; // membership change is hub-owned — reflect to the hub
      actionInfo.bodyParameters = payload as any;
      await dataManager.callAction<MembershipIdentifiers, ApiResponse<void>>(actionInfo);
      // if the user removed himself from the entity, navigate to the landing page
      if (membership.user_id === dataContext.user?.id) {
        void navigator.navigateToLanding(true);
      }
    } catch (error) {
      console.error('Failed to remove Membership', error);
    }
  }

  recalculateActiveEntityMemberships() {
    if (!dataContext.activeEntity?.saved) {
      this.activeEntityMemberships = [];
      return;
    }

    const fetchMembershipsOfActiveEntity = dataContext.activeEntity.typeId;
    void this.fetchMemberships(fetchMembershipsOfActiveEntity).then((memberships) => {
      runInAction(() => {
        if (!dataContext.activeEntity?.typeId) {
          return;
        }
        if (fetchMembershipsOfActiveEntity.equals(dataContext.activeEntity.typeId)) {
          this.activeEntityMemberships = memberships ?? [];
        }
      });
    });
  }

  recalculateActiveWorkspaceMemberships() {
    if (!dataContext.workspace?.saved) {
      this.activeEntityMemberships = [];
      return;
    }

    const fetchMembersForWorkspace = dataContext.workspace.typeId;
    void this.fetchMemberships(fetchMembersForWorkspace).then((memberships) => {
      runInAction(() => {
        if (!dataContext.workspace?.saved) {
          return;
        }
        if (fetchMembersForWorkspace.equals(dataContext.workspace.typeId)) {
          this.activeWorkspaceMemberships = memberships ?? [];
        }
      });
    });
  }
}

export const mentionService = new MentionService();
export const rolesLabel = new RolesLabel();
export const membershipService = new MembershipService();
